import { useCallback, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { matchSorter } from 'match-sorter';
import validator from 'validator';

import {
  Alert,
  Autocomplete,
  Avatar,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  Input,
  InputAdornment,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';

import SearchIcon from '@mui/icons-material/Search';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CloseIcon from '@mui/icons-material/Close';

import { DiligenceAccessStore } from 'stores/diligence/DiligenceAccessStore';

import { diligenceAccessLevelOptions, removeAccessOption } from 'utils/options';
import { DiligenceAccessLevel, UserPreviewOutput } from 'dtos/diligence/DiligenceAccess';

function renderInitials(name: string): string {
  return name.replace(/[^A-Z]/g, '').substring(0, 2);
}

type UserPreviewProps = {
  user: UserPreviewOutput;
  removed?: boolean;
};

type UserOption = UserPreviewOutput & {
  isNew?: boolean;
};

const UserPreview = ({ user, removed, ...rest }: UserPreviewProps) => {
  const styles: React.CSSProperties = {
    display: 'flex',
    gap: 4,
    alignItems: 'center',
    paddingRight: '24px',
    minWidth: 0,
    overflow: 'hidden',
  };

  if (removed) {
    styles.textDecoration = 'line-through';
  }

  return (
    <div key={user.email} style={styles} {...rest}>
      {user.photo ? (
        <Avatar sx={{ width: 32, height: 32 }} src={`data:image/png;base64, ${user.photo}`} />
      ) : (
        <Avatar sx={{ fontSize: 16, width: 32, height: 32 }} alt={user.name}>
          {renderInitials(user.name || '')}
        </Avatar>
      )}
      <div style={{ marginLeft: 8, flex: 1, minWidth: 0, overflow: 'hidden' }}>
        <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
          <strong>{user.name || '-'}</strong>
        </Typography>
        <Typography
          variant="body2"
          sx={{
            marginTop: '2px',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            maxWidth: '100%',
          }}
        >
          {user.email || '-'}
        </Typography>
      </div>
    </div>
  );
};

const renderOption = (props: any, option: UserOption) =>
  option.isNew ? (
    <div style={{ minWidth: 0, overflow: 'hidden', padding: '8px 16px' }} {...props}>
      Share access with <strong style={{ marginLeft: '4px' }}>{option.email}</strong>
    </div>
  ) : (
    <UserPreview user={option} {...props} />
  );

type ShareAccessModalProps = {
  store: DiligenceAccessStore;
  jobNumber?: string;
};

const ShareAccessModal = observer(({ store, jobNumber }: ShareAccessModalProps) => {
  const [inputValue, setInputValue] = useState('');
  const [value, setValue] = useState(null);
  const {
    closing,
    startClosing,
    handleClosed,
    saveModal,
    saveStatus,
    usersPreviewsStatus,
    usersPreviews,
    shareAccessWith,
    sharedWith,
    changeUserAccessLevel,
  } = store;
  const isLoadingPreviews = usersPreviewsStatus !== 'success';
  const isErrorLoadingPreviews = usersPreviewsStatus === 'error';
  const shouldDisableControls = saveStatus === 'loading';

  const filterOptions = useCallback(
    (options: UserOption[], { inputValue }: { inputValue: string }) => {
      const cleanedInput = inputValue.replace(/\s+/g, '').toLowerCase();

      if (cleanedInput.length < 3) {
        return [];
      }

      const emailsToExclude = sharedWith.map((user) => user?.email).filter(Boolean);
      const filteredOptions = options.filter((option) => !emailsToExclude.includes(option.email));

      const matched: UserOption[] = matchSorter(filteredOptions, inputValue, {
        keys: ['name', 'email'],
        threshold: matchSorter.rankings.CONTAINS,
      });

      const isAlreadyExcluded = emailsToExclude.includes(cleanedInput);
      if (!isAlreadyExcluded && !matched.length && validator.isEmail(cleanedInput)) {
        matched.push({
          email: cleanedInput,
          name: '',
          isNew: true,
        });
      }

      return matched;
    },
    [sharedWith]
  );

  return (
    <Dialog
      TransitionProps={{ onExited: handleClosed }}
      open={!closing}
      onClose={() => {
        if (!shouldDisableControls) {
          startClosing();
        }
      }}
      fullWidth
      maxWidth={'sm'}
      PaperProps={{
        sx: {
          position: 'absolute',
          top: '15%',
          left: '50%',
          transform: 'translateX(-50%)',
        },
      }}
      data-testid="share-access-modal"
    >
      <DialogTitle sx={{ borderBottom: (theme) => `1px solid ${theme.palette.divider}` }}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          Share Access to {jobNumber}
          <IconButton
            disabled={shouldDisableControls}
            aria-label="close"
            onClick={startClosing}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </div>
      </DialogTitle>
      <DialogContent sx={{ flexGrow: 1, overflow: 'auto' }}>
        {isErrorLoadingPreviews ? (
          <Alert sx={{ marginTop: '20px' }} severity="error">
            An error occurred while retrieving the user list. Please try again later.
          </Alert>
        ) : (
          <>
            {saveStatus === 'error' && (
              <Alert sx={{ marginTop: '20px' }} severity="error">
                There was an error submitting the form. Please refresh the page and try again.
              </Alert>
            )}
            <Autocomplete
              sx={{ margin: '16px 0' }}
              fullWidth
              value={value}
              inputValue={inputValue}
              popupIcon={null}
              blurOnSelect={true}
              clearIcon={null}
              disabled={isLoadingPreviews || shouldDisableControls}
              options={usersPreviews as UserOption[]}
              noOptionsText={
                (inputValue?.replace(/\s+/g, '') || '').length < 3
                  ? 'Start typing at least 3 characters to search.'
                  : 'No results found. Enter a valid email to invite a new user and share access with them.'
              }
              filterOptions={filterOptions}
              onInputChange={(event, newInputValue) => {
                setInputValue(newInputValue);
              }}
              onChange={(event, selectedOption) => {
                if (selectedOption) {
                  shareAccessWith(selectedOption);
                  setValue(null);
                  setInputValue('');
                }
              }}
              getOptionLabel={(option) => option.email || ''}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  InputLabelProps={{
                    shrink: true,
                  }}
                  placeholder={isLoadingPreviews ? 'Loading users...' : 'Search users'}
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <>
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                        {params.InputProps.startAdornment}
                      </>
                    ),
                  }}
                />
              )}
              renderOption={renderOption}
            />

            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
              <Typography component="div" variant="subtitle1" sx={{ flex: 1 }}>
                <strong>People with access</strong>
              </Typography>
            </div>
            <Box sx={{ display: 'flex', flexDirection: 'column', overflowY: 'auto', maxHeight: '50vh' }}>
              {sharedWith?.length ? (
                sharedWith.map((user) => (
                  <div key={user.email}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <UserPreview user={user} removed={user.accessLevel === 'removeAccess'}></UserPreview>
                      <FormControl variant="standard" sx={{ width: '140px', flexShrink: 0, marginRight: '16px' }}>
                        <Select
                          id={`${user.email}-accessLevel-label`}
                          displayEmpty
                          disabled={shouldDisableControls}
                          size="small"
                          IconComponent={ExpandMoreIcon}
                          input={<Input disableUnderline />}
                          value={user.accessLevel || 'diligence'}
                          onChange={(event) =>
                            changeUserAccessLevel(user.email, event.target?.value as DiligenceAccessLevel)
                          }
                          sx={{
                            '& .MuiSelect-select': {
                              paddingBottom: '0px',
                            },
                          }}
                        >
                          {Object.entries(diligenceAccessLevelOptions).map(([key, value]) => (
                            <MenuItem
                              key={key}
                              value={key}
                              style={{ color: key === removeAccessOption ? 'red' : 'inherit' }}
                            >
                              {value}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </div>
                    <Divider sx={{ margin: '12px 0px' }} />
                  </div>
                ))
              ) : (
                <div>
                  <Typography>You haven't shared access with anyone yet. Use the search bar to add users.</Typography>
                </div>
              )}
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <div style={{ flex: 1 }} />
        <Button
          data-testid="access-modal-cancel"
          variant="outlined"
          onClick={startClosing}
          disabled={shouldDisableControls}
        >
          {isErrorLoadingPreviews ? 'Close' : 'Cancel'}
        </Button>
        {!isErrorLoadingPreviews && (
          <Button
            data-testid="access-modal-send"
            variant="contained"
            disabled={shouldDisableControls || !sharedWith.length}
            onClick={saveModal}
          >
            Send
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
});

export { ShareAccessModal };
